import React from "react";
import { Link } from "react-router-dom";
import FilmFrontLogo from "@/components/FilmFrontLogo";
import { ArrowRight, Camera, Users, Film, MapPin, Handshake, Briefcase, Sparkles, Apple, Play } from "lucide-react";

const FEATURES = [
  { icon: Users, title: "Verified Member Directory", body: "Casting, gaffers, colorists, producers — everyone building the Southern Utah film scene, in one place." },
  { icon: MessageSquareIcon, title: "Community Board", body: "Post gigs, share reels, ask questions. Comments and likes bring the crew together between shoots." },
  { icon: Film, title: "Festival Hub", body: "Deadlines, submissions, and press for every festival in the region — from Kanab to St. George." },
  { icon: MapPin, title: "Production Map", body: "See who's shooting where. Discover crews, locations, and permit info at a glance." },
  { icon: Briefcase, title: "Jobs & Marketplace", body: "Roles, gear rentals, and services posted by verified members." },
  { icon: Handshake, title: "Deal Room (Networking Only)", body: "Investors and filmmakers connect. Strictly networking — no transactions, SEC-safe." },
];

function MessageSquareIcon(props) {
  // Fallback — using generic message icon w/o importing extra
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050505] text-white ff-grain relative overflow-x-hidden">
      {/* Nav */}
      <header className="ff-glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <FilmFrontLogo size="md" />
          <nav className="hidden md:flex items-center gap-6 text-sm text-neutral-300">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#region" className="hover:text-white transition-colors">The Region</a>
            <a href="#apps" className="hover:text-white transition-colors">Mobile Apps</a>
            <a href="#about" className="hover:text-white transition-colors">About</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="ff-btn-ghost text-sm" data-testid="landing-signin-btn">Sign in</Link>
            <Link to="/register" className="ff-btn-primary text-sm" data-testid="landing-join-btn">
              Join Film Front <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10">
        <div className="ff-orange-orb -top-40 -left-40" />
        <div className="ff-orange-orb top-20 right-[-260px] opacity-60" />
        <div className="max-w-[1400px] mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10 animate-fade-up">
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-6 flex items-center gap-2" data-testid="landing-eyebrow">
              <Sparkles className="w-3.5 h-3.5 ff-accent-text" />
              The professional network for Southern Utah film and beyond
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-medium mb-6" data-testid="landing-hero-title">
              Where the desert
              <br />
              <span className="ff-accent-text">makes movies.</span>
            </h1>
            <p className="text-lg text-neutral-300 max-w-xl leading-relaxed mb-10">
              Film Front is the members-only network for crews, producers, festivals, and investors working in Kanab, Moab, St. George, Cedar City, and every canyon in between.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/register" className="ff-btn-primary" data-testid="landing-hero-join">
                Create your profile <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/login" className="ff-btn-ghost" data-testid="landing-hero-signin">
                I already have an account
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#apps" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 hover:border-white/25 bg-black/40 transition-colors" data-testid="landing-appstore-link">
                <Apple className="w-5 h-5" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
              <a href="#apps" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 hover:border-white/25 bg-black/40 transition-colors" data-testid="landing-playstore-link">
                <Play className="w-5 h-5" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 relative z-10">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] max-w-md mx-auto">
              <img
                src="https://images.pexels.com/photos/23384400/pexels-photo-23384400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=980&w=780"
                alt="Cinematic camera on set"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <div className="absolute top-4 left-4 px-2 py-1 rounded-full ff-accent-bg text-black text-[10px] font-semibold uppercase tracking-widest flex items-center gap-1">
                <Camera className="w-3 h-3" /> On location
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Region strip */}
      <section id="region" className="border-y border-white/10 bg-[#0a0908] relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-wrap items-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.25em] text-neutral-400">
          <span className="text-neutral-500">Regions served:</span>
          <span>Kanab</span><span>Moab</span><span>St. George</span><span>Cedar City</span>
          <span>Zion Corridor</span><span>Escalante</span><span>Bryce</span><span>Grand Staircase</span>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-[1400px] mx-auto px-6 py-24 relative z-10">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4">What's inside</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1] font-medium">
            One network. Every department.
          </h2>
          <p className="text-neutral-400 mt-4 text-base leading-relaxed">
            Built with working professionals — not silicon valley. Everything you need to find the next gig, close the next crew, or launch the next indie feature from the Colorado Plateau.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="ff-card p-8 group" data-testid={`landing-feature-${i}`}>
              <div className="w-11 h-11 rounded-lg ff-accent-bg/10 border border-white/10 flex items-center justify-center mb-6 group-hover:ff-accent-border transition-colors">
                <f.icon className="w-5 h-5 ff-accent-text" />
              </div>
              <div className="font-display text-xl mb-2 tracking-tight">{f.title}</div>
              <p className="text-neutral-400 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Big desert banner */}
      <section className="relative">
        <div className="relative h-[420px] overflow-hidden">
          <img
            src="https://images.pexels.com/photos/6983543/pexels-photo-6983543.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600"
            alt="Utah desert at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
          <div className="absolute inset-0 max-w-[1400px] mx-auto px-6 flex items-end pb-12">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-[0.3em] ff-accent-text mb-3">Shot in Southern Utah</div>
              <h3 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight">
                A subsidiary of Film Southern Utah and Media Arts, Inc.
              </h3>
              <p className="text-neutral-300 mt-4 text-sm max-w-md">
                Film Front exists to make it dramatically easier to make films in the most cinematic landscape in the American West.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apps CTA */}
      <section id="apps" className="max-w-[1400px] mx-auto px-6 py-24 relative z-10">
        <div className="ff-card p-10 sm:p-14 flex flex-col lg:flex-row items-center gap-10">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Mobile first</div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              The Film Front app lives in your pocket.
            </h3>
            <p className="text-neutral-400 mt-4 max-w-lg">
              This site is a companion to our iOS and Android app — for on-set members, the mobile app is home. Log the same JWT session across both.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#" className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-white/10 hover:border-white/25 bg-black/40 transition-colors" data-testid="apps-appstore">
                <Apple className="w-5 h-5" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
              <a href="#" className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-white/10 hover:border-white/25 bg-black/40 transition-colors" data-testid="apps-playstore">
                <Play className="w-5 h-5" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Link to="/register" className="ff-btn-primary text-base" data-testid="apps-join-btn">
              Join the network <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <footer id="about" className="ff-hairline">
        <div className="max-w-[1400px] mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <FilmFrontLogo size="sm" />
            <span className="text-xs text-neutral-500">© {new Date().getFullYear()} Film Southern Utah and Media Arts, Inc.</span>
          </div>
          <div className="text-xs text-neutral-500 flex flex-wrap gap-4">
            <span>www.filmsouthernutah.com</span>
            <Link to="/login" className="hover:text-white">Sign in</Link>
            <Link to="/register" className="hover:text-white">Join</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
